<template>
  <Toolbar class="p-mb-4 p-mt-4 p-p-2">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-p-2">
        {{ $t('locationAccountingTitle') }}
      </div>
    </template>
    <template #end>
      <div class="p-p-1">
        <Button @click="saveSettings"
                :disabled="(!canUseAccountingExport && !canUsePeriodLock) || !canManageAccounting || !locationData?.id || locationIsLoading || integrationIsLoading || fetchInFlight"
                :loading="fetchInFlight"
                class="p-button-raised"
                icon="ti-save"
                :label="$t('Save')"/>
      </div>
    </template>
  </Toolbar>

  <div class="p-grid">
    <div class="p-col-12 p-lg-8 p-mb-2">
      <fieldset class="p-fieldset p-pb-4 integration-section">
        <legend class="p-fieldset-legend p-p-2">{{ $t('accountingIntegrationTitle') }}</legend>

        <BillingFeatureNotice v-if="!canUseAccountingExport"
                              feature="accounting_export"
                              :description="$t('billing.features.accounting_export.tooltip')"/>

        <div v-else-if="integrationIsLoading" class="p-d-flex p-jc-center p-py-5">
          <ProgressSpinner class="integration-spinner"/>
        </div>

        <template v-else>
          <small class="p-d-block p-mt-2 p-text-secondary">
            {{ companyScopeText }}
          </small>

          <div class="integration-top-row p-mt-5 p-mb-4">
            <div class="provider-field p-float-label">
              <CustomDropdown id="accounting-provider"
                              v-model="selectedProvider"
                              :options="providers"
                              optionLabel="name"
                              dataKey="id"
                              :showClear="true"
                              :disabled="fetchInFlight"
                              class="settings-input"
                              @change="onProviderChanged"/>
              <label for="accounting-provider">{{ $t('accountingProgramLabel') }}</label>
            </div>

            <div v-if="selectedProviderId" class=" p-d-flex p-ai-center">
              <InputSwitch id="accounting-integration-enabled"
                           class="accounting-switch"
                           v-model="integrationEnabled"
                           :disabled="fetchInFlight"/>
              <label for="accounting-integration-enabled" class="switch-label pointer">
                {{ $t('accountingIntegrationEnabledLabel') }}
              </label>
            </div>
          </div>

          <template v-if="selectedProviderId">
            <InlineMessage :severity="integrationEnabled ? 'success' : 'info'"
                           class="integration-status">
              {{ integrationEnabled
                ? $t('accountingIntegrationConnectedAfterSaveHelp')
                : $t('accountingIntegrationDisconnectedHelp') }}
            </InlineMessage>

            <div>
              <hr class="p-mt-4 p-mb-3">

              <div class="section-heading p-mb-5">{{ $t('accountingCredentialsTitle') }}</div>

              <div class="p-grid credential-grid">
                <div v-for="field in selectedProviderCredentialFields"
                     :key="field.key"
                     class="p-field p-col-12 p-md-6 p-mb-0 credential-field">
                  <div v-if="integrationHasCredentials && !editCredentials"
                       class="p-float-label">
                    <InputText :id="'accounting-credential-' + field.key"
                               modelValue="********" disabled class="settings-input"/>
                    <label :for="'accounting-credential-' + field.key">
                      {{ credentialFieldLabel(field) }}
                    </label>
                  </div>
                  <div v-else class="p-float-label">
                    <Password :id="'accounting-credential-' + field.key"
                              v-model="integrationCredentials[field.key]"
                              :disabled="fetchInFlight"
                              :inputClass="credentialIsInvalid(field) ? 'p-invalid validation-error-target' : ''"
                              autocomplete="new-password"
                              toggleMask
                              :feedback="false"
                              class="settings-input"/>
                    <label :for="'accounting-credential-' + field.key">
                      {{ credentialFieldLabel(field) }}<span v-if="integrationEnabled && field.required && !integrationHasCredentials" class="warning-color">*</span>
                    </label>
                  </div>
                  <small v-if="credentialIsInvalid(field)" class="p-invalid">
                    {{ $t('Required field') }}
                  </small>
                </div>
              </div>

              <div v-if="integrationHasCredentials" class="p-mt-1">
                <div class="p-d-flex p-ai-center p-flex-wrap credential-edit-row">
                  <span class="p-text-secondary green-color">{{ $t('accountingCredentialsSavedLabel') }}</span>
                  <div class="p-d-flex p-ai-center">
                    <InputSwitch id="accounting-edit-credentials"
                                 v-model="editCredentials"
                                 class="accounting-switch"
                                 :disabled="fetchInFlight"/>
                    <label for="accounting-edit-credentials" class="switch-label pointer">
                      {{ $t('accountingCredentialsEditLabel') }}
                    </label>
                  </div>
                </div>

                <small v-if="editCredentials" class="p-d-block p-mt-1 p-text-secondary">
                  {{ $t('accountingCredentialKeepHelp') }}
                </small>
              </div>

              <div v-if="isMeritProvider"
                   :class="integrationHasCredentials ? 'p-mt-6' : 'p-mt-4'">
                <div class="p-field p-mb-0 p-float-label merit-payment-field">
                  <InputText id="merit-cash-payment-method"
                             v-model.trim="integrationCashPaymentMethod"
                             :disabled="fetchInFlight"
                             maxlength="255"
                             class="settings-input"/>
                  <label for="merit-cash-payment-method">{{ $t('cashRegisterNameLabel') }}</label>
                </div>
              </div>

              <div class="p-grid codes-grid">
                <div v-for="direction in availableDirections"
                     :key="direction"
                     :class="canUsePurchaseInvoices ? 'p-col-12 p-xl-6' : 'p-col-12'">
                  <div class="p-fieldset p-p-0 p-mb-0 codes-panel"
                       :class="{'codes-panel--invalid': directionIsInvalid(direction)}">
                    <div class="p-p-3">
                      <div class="codes-panel-header p-d-flex p-jc-between p-ai-center p-mb-2">
                        <div class="section-heading">
                          {{ $t(direction === 'purchase' ? 'purchaseInvoiceCodesTitle' : 'salesInvoiceCodesTitle') }}<span v-if="integrationEnabled" class="warning-color">*</span>
                        </div>

                        <Button type="button"
                                icon="ti-plus"
                                class="p-button-sm p-button-text"
                                :label="$t('Add')"
                                :disabled="fetchInFlight || codeList(direction).length >= maxCodesPerDirection"
                                @click="addCode(direction)"/>
                      </div>

                      <small class="p-d-block p-mb-4 p-text-secondary">
                        {{ $t('accountingCodesLimitHelp', {max: maxCodesPerDirection}) }}
                      </small>

                      <InlineNotice v-if="directionHasNoCodes(direction)"
                                    severity="error"
                                    compact
                                    class="p-mb-3 validation-error-target">
                        {{ $t('accountingCodesEmptyHelp') }}
                      </InlineNotice>
                      <div v-else-if="integrationEnabled && !codeList(direction).length"
                           class="empty-codes p-p-3 p-mb-3 p-text-secondary">
                        {{ $t('accountingCodesEmptyHelp') }}
                      </div>

                      <div v-for="(item, index) in codeList(direction)"
                           :key="direction + '-' + index"
                           class="code-row p-grid p-ai-center">
                        <div class="p-field p-col-12 p-md-4 p-mb-2">
                          <div class="p-float-label">
                            <InputText :id="direction + '-code-' + index"
                                       v-model.trim="item.code"
                                       :disabled="fetchInFlight"
                                       :class="{'p-invalid validation-error-target': codeIsInvalid(direction, index)}"
                                       maxlength="64"
                                       class="settings-input"
                                       @input="onCodeChanged(direction)"/>
                            <label :for="direction + '-code-' + index">{{ $t('accountingCodeLabel') }}</label>
                          </div>
                        </div>

                        <div class="p-field p-col-10 p-md-7 p-mb-2">
                          <div class="p-float-label">
                            <InputText :id="direction + '-description-' + index"
                                       v-model.trim="item.description"
                                       :disabled="fetchInFlight"
                                       :class="{'p-invalid validation-error-target': descriptionIsInvalid(direction, index)}"
                                       maxlength="255"
                                       class="settings-input"/>
                            <label :for="direction + '-description-' + index">{{ $t('accountingCodeDescriptionLabel') }}</label>
                          </div>
                        </div>

                        <div class="p-col-2 p-md-1 p-d-flex p-jc-end p-mb-2">
                          <Button type="button"
                                  icon="ti-trash"
                                  class="p-button-sm p-button-text p-button-danger"
                                  :disabled="fetchInFlight"
                                  @click="removeCode(direction, index)"/>
                        </div>
                      </div>

                      <template v-if="codeList(direction).length">
                        <div class="defaults-block p-mt-4">
                          <div class="section-subheading p-mb-5">{{ $t('defaultAccountingCodesTitle') }}</div>

                          <div v-for="positionType in positionTypes"
                               :key="direction + '-default-' + positionType"
                               class="p-field p-mb-5">
                            <div class="p-float-label">
                              <CustomDropdown :id="direction + '-default-' + positionType"
                                              v-model="integrationSettings[direction].defaults[positionType]"
                                              :options="validCodeOptions(direction)"
                                              optionLabel="label"
                                              optionValue="code"
                                              :filter="true"
                                              :filterFields="['code', 'description', 'label']"
                                              :showClear="true"
                                              :disabled="fetchInFlight"
                                              :class="{'p-invalid validation-error-target': defaultIsInvalid(direction, positionType)}"
                                              class="settings-input"/>
                              <label :for="direction + '-default-' + positionType">
                                {{ defaultCodeLabel(direction, positionType) }}<span v-if="integrationEnabled" class="warning-color">*</span>
                              </label>
                            </div>
                            <small v-if="defaultIsInvalid(direction, positionType)" class="p-invalid">
                              {{ $t('Required field') }}
                            </small>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </fieldset>
    </div>

    <div class="p-col-12 p-lg-4">
      <fieldset class="p-fieldset p-pb-4 p-mb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('defaultExportInvoicesTitle') }}</legend>

        <div class="p-d-flex p-ai-center p-mt-3 export-toggle">
          <InputSwitch id="default-export-sales-invoice"
                       class="accounting-switch"
                       :modelValue="canSelectSalesDefault && defaultExportSalesInvoice"
                       @update:modelValue="defaultExportSalesInvoice = $event"
                       :disabled="!canEditSalesDefault"/>
          <label for="default-export-sales-invoice"
                 class="switch-label"
                 :class="{ pointer: canEditSalesDefault }">
            {{ $t('defaultExportSalesInvoiceLabel') }}
          </label>
        </div>

        <BillingFeatureHint v-if="!canUseAccountingExport"
                            feature="accounting_export"
                            class="p-d-block p-mt-2"/>

        <div class="p-d-flex p-ai-center p-mt-4 export-toggle">
          <InputSwitch id="default-export-purchase-invoice"
                       class="accounting-switch"
                       :modelValue="canSelectPurchaseDefault && defaultExportPurchaseInvoice"
                       @update:modelValue="defaultExportPurchaseInvoice = $event"
                       :disabled="!canEditPurchaseDefault"/>
          <label for="default-export-purchase-invoice"
                 class="switch-label"
                 :class="{ pointer: canEditPurchaseDefault }">
            {{ $t('defaultExportPurchaseInvoiceLabel') }}
          </label>
        </div>

        <BillingFeatureHint v-if="!canUsePurchaseInvoices"
                            feature="purchase_invoices"
                            class="p-d-block p-mt-2"/>
      </fieldset>

      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('accountingPeriodTitle') }}</legend>

        <!--        {{ $t('accountingPeriodLockedUntilLabel') }}-->
        <!--        <div class="financial-period-setting p-mt-5">-->
        <!--          <div class="financial-period-control p-float-label">-->
        <!--            <Calendar v-model="financialPeriodLockedUntil"-->
        <!--                      :disabled="!canUsePeriodLock || locationIsLoading || fetchInFlight"-->
        <!--                      id="financial-period-locked-until"-->
        <!--                      class="financial-period-calendar"-->
        <!--                      :showButtonBar="true"-->
        <!--                      :selectOtherMonths="true"-->
        <!--                      :dateFormat="settings.dateFormat"-->
        <!--                      showIcon-->
        <!--                      autocomplete="off"-->
        <!--                      @date-select="setFinancialPeriodLockedUntil"/>-->
        <!--            <label for="financial-period-locked-until">{{ $t('accountingPeriodLockedUntilLabel') }}</label>-->
        <!--          </div>-->


        <div class="financial-period-setting p-mt-2">
          {{ $t('accountingPeriodLockedUntilLabel') }}
          <div class="financial-period-control">
            <Calendar v-model="financialPeriodLockedUntil"
                      :disabled="!canUsePeriodLock || locationIsLoading || fetchInFlight"
                      id="financial-period-locked-until"
                      class="financial-period-calendar"
                      :showButtonBar="true"
                      :selectOtherMonths="true"
                      :dateFormat="settings.dateFormat"
                      showIcon
                      autocomplete="off"
                      @date-select="setFinancialPeriodLockedUntil"/>
          </div>

          <small class="p-d-block p-mt-2 p-text-secondary">
            {{ $t('accountingPeriodProtectionHelp') }}
            <span class="p-d-block">{{ companyScopeText }}</span>
          </small>

          <BillingFeatureHint v-if="!canUsePeriodLock"
                              feature="period_lock"
                              class="p-d-block p-mt-3"/>
        </div>
      </fieldset>
    </div>

  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import calculateMixins from '@/mixins/calculateMixins'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureHint from '@/components/billing/BillingFeatureHint.vue'
import BillingFeatureNotice from '@/components/billing/BillingFeatureNotice.vue'
import InlineNotice from '@/components/InlineNotice.vue'

const DIRECTIONS = ['sales', 'purchase']
const POSITION_TYPES = ['product', 'service', 'consumable']
const SMARTACCOUNTS_PROVIDER_ID = 1
const MERIT_PROVIDER_ID = 2

const INTEGRATION_TEMPLATES = {
  [SMARTACCOUNTS_PROVIDER_ID]: {
    sales: {
      codes: [
        {code: '0010', description: 'Kaup'},
        {code: '0020', description: 'Teenus'},
        {code: '0090', description: 'Kaup EL'},
        {code: '0100', description: 'Teenus EL'},
      ],
      defaults: {product: '0010', service: '0020', consumable: '0010'},
    },
    purchase: {
      codes: [
        {code: '00010', description: 'Kaubad, materjal, teenused'},
        {code: '00020', description: 'Kauba transpordikulu'},
      ],
      defaults: {product: '00010', service: '00010', consumable: '00010'},
    },
  },
  [MERIT_PROVIDER_ID]: {
    cashPaymentMethod: 'Kassa',
    sales: {
      codes: [
        {code: '24% kaup', description: 'Kauba müük 24%'},
        {code: '24% teen', description: 'Teenuse müük 24%'},
        {code: '9% müük', description: 'Kauba, teenuse müük 9%'},
        {code: 'EL 0% kaup', description: 'Kauba müük EL 0%'},
        {code: 'EL 0% teen', description: 'Teenuse müük EL 0%'},
      ],
      defaults: {product: '24% kaup', service: '24% teen', consumable: '24% kaup'},
    },
    purchase: {
      codes: [
        {code: 'teen', description: 'Ostetud teenused edasimüügiks'},
        {code: 'kaup', description: 'Ostetud kaubad müügiks - kuluks'},
        {code: 'transp', description: 'Transpordikulu'},
      ],
      defaults: {product: 'kaup', service: 'teen', consumable: 'kaup'},
    },
  },
}

function blankDirectionSettings() {
  return {
    codes: [],
    defaults: {product: null, service: null, consumable: null},
  }
}

function blankIntegrationSettings() {
  return {
    purchase: blankDirectionSettings(),
    sales: blankDirectionSettings(),
  }
}

export default {
  components: { BillingFeatureHint, BillingFeatureNotice, InlineNotice },
  mixins: [showErrorsMixins, calculateMixins, billingFeaturesMixins],
  emits: ['update-items'],
  props: {
    locationData: {type: Object, default: null},
    locationIsLoading: {type: Boolean, default: false},
  },
  data() {
    return {
      settings,
      positionTypes: POSITION_TYPES,
      financialPeriodLockedUntil: null,
      defaultExportSalesInvoice: true,
      defaultExportPurchaseInvoice: true,
      fetchInFlight: false,
      integrationIsLoading: false,
      integrationCompanyName: '',
      providers: [],
      integrations: [],
      supportsInactiveSave: false,
      activeIntegrationId: null,
      selectedProvider: null,
      integrationEnabled: false,
      integrationHasCredentials: false,
      editCredentials: false,
      integrationCredentials: {},
      integrationCashPaymentMethod: '',
      integrationSettings: blankIntegrationSettings(),
      maxCodesPerDirection: 10,
      submitted: false,
      integrationRequestId: 0,
    }
  },
  computed: {
    companyScopeText() {
      const company = this.integrationCompanyName || this.locationData?.company_name
      return company
          ? this.$t('companyScopeHelp', {company})
          : this.$t('companyScopeFallbackHelp')
    },
    canUseAccountingExport() {
      return this.locationData?.accounting_export_available === true
    },
    canUsePurchaseInvoices() {
      return this.locationData?.purchase_invoices_available === true
    },
    canSelectSalesDefault() {
      return this.canUseAccountingExport && !!this.selectedProviderId && this.integrationEnabled
    },
    canSelectPurchaseDefault() {
      return this.canSelectSalesDefault && this.canUsePurchaseInvoices
    },
    canEditSalesDefault() {
      return this.canSelectSalesDefault && this.canManageAccounting
          && !this.fetchInFlight && !this.locationIsLoading && !this.integrationIsLoading
    },
    canEditPurchaseDefault() {
      return this.canSelectPurchaseDefault && this.canManageAccounting
          && !this.fetchInFlight && !this.locationIsLoading && !this.integrationIsLoading
    },
    availableDirections() {
      return this.canUsePurchaseInvoices ? DIRECTIONS : ['sales']
    },
    canUsePeriodLock() {
      return this.locationData?.accounting_period_lock_available === true
    },
    canManageAccounting() {
      return this.$store.getters.isAccountantOrHigher
    },
    selectedProviderId() {
      return this.selectedProvider?.id ?? null
    },
    selectedProviderCredentialFields() {
      return (this.selectedProvider?.credential_fields || []).filter(field => field.key !== 'cashPaymentMethod')
    },
    isMeritProvider() {
      return +this.selectedProviderId === MERIT_PROVIDER_ID
    },
  },
  watch: {
    editCredentials(enabled) {
      if (enabled || !this.integrationHasCredentials) return
      this.selectedProviderCredentialFields.forEach(field => {
        delete this.integrationCredentials[field.key]
      })
    },
    'locationData.financial_period_locked_until': {
      immediate: true,
      handler(value) {
        this.financialPeriodLockedUntil = value ? new Date(Number(value) * 1000) : null
      },
    },
    'locationData.default_export_sales_invoice': {
      immediate: true,
      handler(value) {
        this.defaultExportSalesInvoice = !!+value
      },
    },
    'locationData.default_export_purchase_invoice': {
      immediate: true,
      handler(value) {
        this.defaultExportPurchaseInvoice = !!+value
      },
    },
    'locationData.id': {
      immediate: true,
      handler(value) {
        this.integrationRequestId++
        this.resetIntegrationState()
        if (value && this.canUseAccountingExport) this.loadIntegrationSettings()
      },
    },
    'locationData.accounting_export_available'(enabled) {
      if (enabled && this.locationData?.id && !this.integrationIsLoading) this.loadIntegrationSettings()
      if (!enabled) {
        this.integrationRequestId++
        this.integrationIsLoading = false
        this.resetIntegrationState()
      }
    },
  },
  methods: {
    resetIntegrationState() {
      this.integrationCompanyName = ''
      this.providers = []
      this.integrations = []
      this.supportsInactiveSave = false
      this.activeIntegrationId = null
      this.selectedProvider = null
      this.integrationEnabled = false
      this.integrationHasCredentials = false
      this.editCredentials = false
      this.integrationCredentials = {}
      this.integrationCashPaymentMethod = ''
      this.integrationSettings = blankIntegrationSettings()
      this.submitted = false
    },
    normalizeSettings(source) {
      const result = blankIntegrationSettings()

      DIRECTIONS.forEach(direction => {
        const directionData = source?.[direction] || {}
        result[direction].codes = Array.isArray(directionData.codes)
            ? directionData.codes.map(item => ({
              code: String(item?.code ?? ''),
              description: String(item?.description ?? ''),
            }))
            : []

        POSITION_TYPES.forEach(positionType => {
          const value = directionData?.defaults?.[positionType]
          result[direction].defaults[positionType] = value == null ? null : String(value)
        })
      })

      return result
    },
    async loadIntegrationSettings() {
      const locationId = this.locationData?.id
      if (!locationId || !this.canManageAccounting || !this.canUseAccountingExport) return

      const requestId = ++this.integrationRequestId
      this.integrationIsLoading = true
      this.submitted = false

      try {
        const {status, data} = await httpClient({
          url: 'accounting-integration/settings',
          method: 'get',
          params: {location_id: locationId},
        })

        if (requestId !== this.integrationRequestId) return

        if (status === 200 && data?.success) {
          this.integrationCompanyName = data.company?.name || ''
          this.providers = Array.isArray(data.providers) ? data.providers : []
          this.integrations = Array.isArray(data.integrations) ? data.integrations : []
          this.supportsInactiveSave = data.supports_inactive_save === true
          this.activeIntegrationId = data.active_integration_id ?? null
          this.maxCodesPerDirection = Number(data.max_codes_per_direction || 10)

          const activeIntegration = this.integrations.find(item => item.is_active)
          const initialIntegration = activeIntegration || this.integrations[0] || null

          if (initialIntegration) {
            this.selectedProvider = this.providers.find(
                provider => +provider.id === +initialIntegration.provider
            ) || null
            this.applyIntegration(initialIntegration, !!initialIntegration.is_active)
          } else {
            this.selectedProvider = null
            this.integrationEnabled = false
            this.integrationHasCredentials = false
            this.editCredentials = false
            this.integrationCredentials = {}
            this.integrationCashPaymentMethod = ''
            this.integrationSettings = blankIntegrationSettings()
          }
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        if (requestId === this.integrationRequestId) this.showError(err)
      } finally {
        if (requestId === this.integrationRequestId) this.integrationIsLoading = false
      }
    },
    applyIntegration(integration, enabled = false) {
      this.integrationEnabled = enabled
      this.integrationHasCredentials = !!integration?.has_credentials
      this.editCredentials = false
      this.integrationCredentials = {}
      this.integrationCashPaymentMethod = String(integration?.settings?.cashPaymentMethod ?? '')
      this.integrationSettings = this.normalizeSettings(integration?.settings)
      this.submitted = false
    },
    onProviderChanged(event) {
      const rawValue = event && Object.prototype.hasOwnProperty.call(event, 'value')
          ? event.value
          : this.selectedProvider

      this.selectedProvider = rawValue && typeof rawValue === 'object'
          ? rawValue
          : this.providers.find(provider => +provider.id === +rawValue) || null

      this.integrationCredentials = {}
      this.integrationCashPaymentMethod = ''
      this.editCredentials = false
      this.submitted = false

      if (!this.selectedProviderId) {
        this.integrationEnabled = false
        this.integrationHasCredentials = false
        this.integrationSettings = blankIntegrationSettings()
        return
      }

      const saved = this.integrations.find(item => +item.provider === +this.selectedProviderId)
      if (saved) {
        this.applyIntegration(saved, !!saved.is_active)
      } else {
        this.integrationEnabled = true
        this.integrationHasCredentials = false
        const template = INTEGRATION_TEMPLATES[this.selectedProviderId]
        this.integrationCashPaymentMethod = String(template?.cashPaymentMethod ?? '')
        this.integrationSettings = this.normalizeSettings(template)
      }
    },
    setFinancialPeriodLockedUntil() {
      if (this.financialPeriodLockedUntil) {
        this.financialPeriodLockedUntil = this.calculateDayEndDate(this.financialPeriodLockedUntil)
      }
    },
    codeList(direction) {
      return this.integrationSettings?.[direction]?.codes || []
    },
    validCodeOptions(direction) {
      return this.codeList(direction)
          .filter(item => item.code && item.description)
          .map(item => ({
            ...item,
            label: this.formatCodeOption(item),
          }))
    },
    findCode(direction, code) {
      return this.codeList(direction).find(item => String(item.code) === String(code)) || null
    },
    formatCodeOption(item) {
      if (!item) return ''
      const code = String(item.code || '').trim()
      const description = String(item.description || '').trim()
      return code && description ? `${code} - ${description}` : (code || description)
    },
    addCode(direction) {
      if (this.codeList(direction).length >= this.maxCodesPerDirection) return
      this.integrationSettings[direction].codes.push({code: '', description: ''})
    },
    removeCode(direction, index) {
      const list = this.codeList(direction)
      const removedCode = list[index]?.code
      list.splice(index, 1)

      if (removedCode) {
        POSITION_TYPES.forEach(positionType => {
          if (this.integrationSettings[direction].defaults[positionType] === removedCode) {
            this.integrationSettings[direction].defaults[positionType] = null
          }
        })
      }
    },
    onCodeChanged(direction) {
      const availableCodes = this.codeList(direction).map(item => String(item.code || ''))
      POSITION_TYPES.forEach(positionType => {
        const selected = this.integrationSettings[direction].defaults[positionType]
        if (selected && !availableCodes.includes(String(selected))) {
          this.integrationSettings[direction].defaults[positionType] = null
        }
      })
    },
    duplicateCode(direction, index) {
      const current = String(this.codeList(direction)[index]?.code || '').trim().toLocaleLowerCase()
      if (!current) return false
      return this.codeList(direction).some((item, itemIndex) => (
          itemIndex !== index && String(item?.code || '').trim().toLocaleLowerCase() === current
      ))
    },
    directionHasNoCodes(direction) {
      return !!(this.submitted && this.integrationEnabled && !this.codeList(direction).length)
    },
    directionIsInvalid(direction) {
      if (!this.submitted || !this.integrationEnabled) return false

      const codes = this.codeList(direction)
      if (!codes.length || codes.length > this.maxCodesPerDirection) return true

      const rowsInvalid = codes.some((item, index) => (
          !String(item?.code || '').trim()
          || !String(item?.description || '').trim()
          || this.duplicateCode(direction, index)
      ))
      if (rowsInvalid) return true

      return POSITION_TYPES.some(positionType => {
        const value = this.integrationSettings[direction].defaults[positionType]
        return !value || !this.findCode(direction, value)
      })
    },
    focusFirstValidationError() {
      this.$nextTick(() => {
        const target = this.$el?.querySelector('.validation-error-target, .p-invalid')
        if (target?.scrollIntoView) {
          target.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
      })
    },
    codeIsInvalid(direction, index) {
      if (!this.submitted || !this.integrationEnabled) return false
      return !String(this.codeList(direction)[index]?.code || '').trim() || this.duplicateCode(direction, index)
    },
    descriptionIsInvalid(direction, index) {
      if (!this.submitted || !this.integrationEnabled) return false
      return !String(this.codeList(direction)[index]?.description || '').trim()
    },
    defaultIsInvalid(direction, positionType) {
      if (!this.submitted || !this.integrationEnabled) return false
      const value = this.integrationSettings[direction].defaults[positionType]
      return !value || !this.findCode(direction, value)
    },
    credentialIsInvalid(field) {
      if (!this.submitted || !this.integrationEnabled || !field.required || this.integrationHasCredentials) return false
      return !String(this.integrationCredentials[field.key] || '').trim()
    },
    credentialFieldLabel(field) {
      if (!field?.label_key || field.label_key === field.label) return String(field?.label || '')
      return this.$t(field.label_key)
    },
    defaultCodeLabel(direction, positionType) {
      const keys = {
        purchase: {
          product: 'purchasedProductCodeLabel',
          service: 'purchasedServiceCodeLabel',
          consumable: 'purchasedConsumableCodeLabel',
        },
        sales: {
          product: 'soldProductCodeLabel',
          service: 'soldServiceCodeLabel',
          consumable: 'soldConsumableCodeLabel',
        },
      }
      return this.$t(keys[direction][positionType])
    },
    integrationDataIsValid() {
      if (!this.integrationEnabled) return true
      if (!this.selectedProviderId) return false

      const credentialsValid = this.selectedProviderCredentialFields.every(field => (
          !field.required || this.integrationHasCredentials || String(this.integrationCredentials[field.key] || '').trim()
      ))
      if (!credentialsValid) return false

      return this.availableDirections.every(direction => {
        const codes = this.codeList(direction)
        if (!codes.length || codes.length > this.maxCodesPerDirection) return false

        const rowsValid = codes.every((item, index) => (
            String(item.code || '').trim()
            && String(item.description || '').trim()
            && !this.duplicateCode(direction, index)
        ))
        if (!rowsValid) return false

        return POSITION_TYPES.every(positionType => {
          const value = this.integrationSettings[direction].defaults[positionType]
          return !!value && !!this.findCode(direction, value)
        })
      })
    },
    async saveLocationAccountingSettings(locationId) {
      const payload = {
        location_id: locationId,
      }

      if (this.canUseAccountingExport) {
        payload.default_export_sales_invoice = this.defaultExportSalesInvoice ? 1 : 0
        if (this.canUsePurchaseInvoices) {
          payload.default_export_purchase_invoice = this.defaultExportPurchaseInvoice ? 1 : 0
        }
      }

      if (this.canUsePeriodLock) {
        this.setFinancialPeriodLockedUntil()
        const timestamp = this.financialPeriodLockedUntil
            ? Math.floor(this.financialPeriodLockedUntil.getTime() / 1000)
            : null

        if (timestamp !== null && (!Number.isInteger(timestamp) || timestamp <= 0)) {
          throw new Error('Invalid financial period date')
        }

        payload.financial_period_locked_until = timestamp
      }

      const {data, status} = await httpClient.post('location/settings-update', payload)

      if (status !== 200 || !data?.success) {
        throw new Error('Unable to save accounting location settings')
      }
    },
    async saveIntegration(locationId) {
      if (this.selectedProviderId) {
        // An older API would silently activate the integration on every save.
        if (!this.integrationEnabled && !this.supportsInactiveSave) {
          throw new Error('Saving an inactive integration requires an updated API.')
        }

        const normalizedSettings = this.normalizeSettings(this.integrationSettings)
        const {data, status} = await httpClient.post('accounting-integration/save', {
          location_id: locationId,
          provider: this.selectedProviderId,
          activate: this.integrationEnabled,
          credentials: {...this.integrationCredentials},
          settings: {
            ...(this.canUsePurchaseInvoices ? normalizedSettings : {sales: normalizedSettings.sales}),
            ...(this.isMeritProvider ? {cashPaymentMethod: this.integrationCashPaymentMethod.trim()} : {}),
          },
        })

        if (status !== 200 || !data?.success || !data.integration) {
          throw new Error('Unable to save accounting integration')
        }
        if (!!data.integration.is_active !== this.integrationEnabled) {
          throw new Error('The saved integration activation state is incorrect.')
        }

        const saved = data.integration
        const index = this.integrations.findIndex(item => +item.provider === +saved.provider)
        if (index >= 0) this.integrations.splice(index, 1, saved)
        else this.integrations.push(saved)

        this.activeIntegrationId = data.active_integration_id ?? null
        this.applyIntegration(saved, !!saved.is_active)

        this.$store.commit('setAccountingIntegration', data.accounting_integration ?? null)

        return
      }

      if (this.activeIntegrationId) {
        const {data, status} = await httpClient.post(
            'accounting-integration/disconnect',
            {
              location_id: locationId,
            }
        )

        if (status !== 200 || !data?.success) {
          throw new Error('Unable to disconnect accounting integration')
        }

        this.activeIntegrationId = null
        this.integrations = this.integrations.map(item => ({
          ...item,
          is_active: false,
        }))
      }

      this.$store.commit('setAccountingIntegration', null)
    },
    async saveSettings() {
      const locationId = this.locationData?.id
      if (!locationId || this.fetchInFlight || this.locationIsLoading || !this.canManageAccounting
          || (!this.canUseAccountingExport && !this.canUsePeriodLock)) return

      this.submitted = true
      if (this.canUseAccountingExport && !this.integrationDataIsValid()) {
        this.focusFirstValidationError()
        return
      }

      this.fetchInFlight = true
      try {
        if (this.canUseAccountingExport) await this.saveIntegration(locationId)
        await this.saveLocationAccountingSettings(locationId)

        this.$emit('update-items', locationId)
        this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife,})
      } catch (err) {
        this.showError(err)
      } finally {
        this.fetchInFlight = false
      }
    },
  },
}
</script>

<style scoped>
.financial-period-setting { width: 100%; }
.financial-period-control { display: flex; align-items: stretch; gap: 0.5rem; width: 100%; max-width: 340px; margin-top: 6px;}
.financial-period-calendar { flex: 1 1 auto; min-width: 0; }
.financial-period-calendar :deep(.p-inputtext) { width: 100%; min-width: 0; }
.p-grid > div,
.p-fieldset { min-width: 0; }
.settings-input { width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box; }
.settings-input :deep(.p-dropdown-label) { min-width: 0; }
.integration-spinner { width: 42px; height: 42px; }
.integration-status { display: flex; width: 100%; }
.accounting-switch { flex: 0 0 auto; }
.export-toggle { min-width: 0; }
.credential-edit-row { column-gap: 1rem; row-gap: 0.5rem; }
.credential-grid { margin-bottom: 0; row-gap: 1rem; }
.codes-grid { margin-top: 1.5rem; }

.integration-top-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.provider-field {
  flex: 0 1 300px;
  min-width: 220px;
}
.section-heading { font-size: 1.05rem; font-weight: 600; line-height: 1.3; }
.section-subheading { font-size: 1rem; font-weight: 600; line-height: 1.3; }

.credential-field :deep(.p-password),
.credential-field :deep(.p-password-input) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.merit-payment-field { max-width: 420px; }

.codes-panel {
  min-width: 0;
}
.codes-panel-header { min-height: 34px; flex-wrap: wrap; gap: 0.5rem; }
.codes-panel-header .section-heading { min-width: 0; overflow-wrap: anywhere; }
.codes-panel-header :deep(.p-button) { flex: 0 0 auto; }
.empty-codes {
  border: 1px dashed var(--surface-border);
  border-radius: 4px;
}
.codes-panel--invalid > .codes-panel-header .section-heading {
  color: var(--red-600, #e53935);
}
.code-row {
  padding-top: 0.5rem;
  border-bottom: 1px solid var(--surface-border);
}
.code-row:last-of-type { border-bottom: 0; }
.defaults-block {
  padding-top: 0.25rem;
  border-top: 1px solid var(--surface-border);
}

@media screen and (max-width: 575px) {
  .integration-top-row { align-items: flex-start; }
  .provider-field { flex-basis: 100%; min-width: 0; }
}
</style>
